import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Phone, Mail, ArrowRight, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';

// 3D Floating Phone Icon
const FloatingPhone3D = ({ scrollYProgress }) => {
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-30, 0, 30]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [20, 0, -20]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <motion.div
      className="absolute hidden xl:block pointer-events-none"
      style={{
        left: '10%',
        top: '30%',
        rotateY,
        rotateX,
        y,
        transformStyle: 'preserve-3d',
      }}
    >
      <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-xl">
        <Phone className="w-8 h-8 text-white" />
      </div>
    </motion.div>
  );
};

// 3D Floating Mail Icon
const FloatingMail3D = ({ scrollYProgress }) => {
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [30, 0, -30]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [-20, 0, 20]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <motion.div
      className="absolute hidden xl:block pointer-events-none"
      style={{
        right: '10%',
        bottom: '30%',
        rotateY,
        rotateX,
        y,
        transformStyle: 'preserve-3d',
      }}
    >
      <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-xl">
        <Mail className="w-8 h-8 text-white" />
      </div>
    </motion.div>
  );
};

// 3D Water Splash Effect
const WaterSplash3D = ({ position, delay }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={position}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: [0, 1.5, 0],
        opacity: [0, 0.6, 0],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        delay,
        ease: 'easeOut',
      }}
    >
      <div className="w-20 h-20 rounded-full border-2 border-white/30" />
    </motion.div>
  );
};

export const CTASection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['10%', '-10%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  
  const titleY = useTransform(scrollYProgress, [0, 0.5, 1], [60, 0, -60]);
  const titleScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]);
  const buttonsY = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.5, 1, 1, 0.5]);

  const springConfig = { stiffness: 100, damping: 30 };
  const titleYSpring = useSpring(titleY, springConfig);
  const buttonsYSpring = useSpring(buttonsY, springConfig);

  return (
    <section 
      ref={containerRef}
      className="py-20 sm:py-28 lg:py-40 relative overflow-hidden"
      data-testid="cta-section"
    >
      {/* Background */}
      <motion.div 
        style={{ y, scale }}
        className="absolute inset-0 bg-gradient-to-br from-sky-500 via-sky-600 to-blue-700"
      />
      
      {/* Wave top */}
      <div className="absolute top-0 left-0 right-0 h-12 sm:h-16 lg:h-20 overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
          <path d="M0,120 C150,30 350,120 500,70 C650,20 800,110 1000,40 C1100,10 1150,30 1200,30 L1200,0 L0,0 Z" fill="white" />
        </svg>
      </div>

      {/* 3D Floating Icons */}
      <FloatingPhone3D scrollYProgress={scrollYProgress} />
      <FloatingMail3D scrollYProgress={scrollYProgress} />

      {/* Water splashes */}
      <WaterSplash3D position={{ left: '20%', top: '40%' }} delay={0} />
      <WaterSplash3D position={{ right: '25%', top: '30%' }} delay={1} />
      <WaterSplash3D position={{ left: '30%', bottom: '35%' }} delay={2} />

      {/* Floating circles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white/10"
            style={{
              width: 10 + Math.random() * 40,
              height: 10 + Math.random() * 40,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          style={{ opacity }}
          className="text-center"
        >
          <motion.div
            style={{ y: titleYSpring, scale: titleScale }}
            className="mb-8 sm:mb-10 lg:mb-12"
          >
            <motion.div
              className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-white/20 backdrop-blur-sm mb-6 sm:mb-8 lg:mb-10"
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Droplets className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
            </motion.div>

            <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 sm:mb-6 tracking-tight px-4">
              ¿Listo para tu nueva piscina?
            </h2>

            <p className="text-base sm:text-lg lg:text-xl text-sky-100 max-w-2xl mx-auto leading-relaxed px-4">
              Contacta hoy y recibe un presupuesto personalizado sin compromiso.
            </p>
          </motion.div>

          <motion.div
            style={{ y: buttonsYSpring }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8 sm:mb-10 lg:mb-12 px-4"
          >
            <Link
              to="/contacto"
              data-testid="cta-contact"
              className="group relative overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 sm:gap-3 bg-white text-sky-600 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-full font-bold text-sm sm:text-base lg:text-lg shadow-2xl"
            >
              <span className="relative z-10">Solicitar Presupuesto</span>
              <motion.div
                className="relative z-10"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.div>
              <motion.div
                className="absolute inset-0 bg-sky-50"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
            
            <a
              href="tel:+34634060878"
              data-testid="cta-phone"
              className="w-full sm:w-auto flex items-center justify-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-full font-bold text-sm sm:text-base lg:text-lg border border-white/30 hover:bg-white/20 transition-all"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              +34 634 06 08 78
            </a>
          </motion.div>

          {/* Quick contact */}
          <motion.div 
            style={{ 
              opacity: useTransform(scrollYProgress, [0.3, 0.5], [0, 1]),
            }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sky-100 text-sm sm:text-base"
          >
            <a 
              href="tel:+34634060878"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>+34 634 06 08 78</span>
            </a>
            <div className="hidden sm:block w-px h-6 bg-sky-400/50" />
            <a 
              href="mailto:genovespiscinas@gmail.com"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="truncate">genovespiscinas@gmail.com</span>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
